import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import Header from "components/Header/Header.js";
import HeaderLinks from "components/Header/HeaderLinks.js";
//import Footer from "components/Footer/Footer.js";
import CustomFooter from "components/CustomFooter/CustomFooter.js";
import GridContainer from "components/Grid/GridContainer.js";
import GridItem from "components/Grid/GridItem.js";
import Card from "components/Card/Card.js";
import CardBody from "components/Card/CardBody.js";
import CardHeader from "components/Card/CardHeader.js";
import Button from "components/CustomButtons/Button.js";
import blogApi from "services/blogApi.js";
import "assets/css/blog-content.css";

const useStyles = makeStyles({
  main: {
    background: "#FFFFFF",
    position: "relative",
    zIndex: "3",
    minHeight: "100vh"
  },
  container: {
    paddingTop: "96px",
    paddingBottom: "50px",
    maxWidth: "800px",
    margin: "0 auto",
    color: "#3f444b"
  },
  backLink: {
    marginBottom: "30px",
    display: "flex",
  },
  backButton: {
    backgroundColor: "transparent",
    color: "#f7931e",
    "&:hover": {
      color: "#f7931eaa",
      backgroundColor: "transparent",
    },
    padding: "4px",
    display: "flex",
    gap: "4px",
    alignItems: "center"
  },
  postCard: {
    boxShadow: "none",
    border: "none",
    backgroundColor: "transparent"
  },
  postTitle: {
    fontFamily: "'Adelle-sans', 'Helvetica Neue', Arial, sans-serif",
    fontSize: "44px",
    fontWeight: "700",
    lineHeight: "1.3em",
    letterSpacing: "-0.3px",
    marginBottom: "20px",
    color: "#3f444b"
  },
  featuredImage: {
    width: "100%",
    height: "auto",
    maxHeight: "400px",
    objectFit: "cover",
    borderRadius: "8px",
    marginBottom: "30px"
  },
  postMeta: {
    fontSize: "0.875rem",
    color: "#999",
    marginBottom: "30px",
    borderBottom: "1px solid #eee",
    paddingBottom: "15px"
  },
  authorSection: {
    display: "flex",
    alignItems: "center",
    marginBottom: "10px",
    gap: "10px"
  },
  authorAvatar: {
    width: "32px",
    height: "32px",
    borderRadius: "50%",
    objectFit: "cover"
  },
  authorName: {
    fontWeight: "500",
    color: "#666"
  },
  tagsSection: {
    marginTop: "10px"
  },
  tag: {
    display: "inline-block",
    backgroundColor: "#f0f0f0",
    color: "#666",
    padding: "4px 8px",
    borderRadius: "12px",
    fontSize: "0.75rem",
    marginRight: "6px",
    marginBottom: "4px"
  },
  postContent: {
    "& .blog-content": {
      "& p": {
        color: "#999999",
        fontSize: "16px"
      },
      "& a": {
        color: "#61b4e4",
        textDecoration: "none",
        "&:hover": {
          color: "#61b4e4aa"
        },
        "&:visited": {
          color: "#4a8fc4"
        }
      }
    }
  },
  loadingText: {
    textAlign: "center",
    fontSize: "1.2rem",
    color: "#999",
    marginTop: "100px"
  },
  errorText: {
    textAlign: "center",
    fontSize: "1.2rem",
    color: "#f44336",
    marginTop: "100px"
  },
  notFoundText: {
    textAlign: "center",
    fontSize: "1.5rem",
    color: "#999",
    marginTop: "100px"
  },
  relatedPostsSection: {
    marginTop: "60px",
    paddingTop: "40px",
    borderTop: "1px solid #eee"
  },
  relatedPostsTitle: {
    fontSize: "1.8rem",
    fontWeight: "600",
    marginBottom: "30px",
    color: "#3f444b"
  },
  relatedPostCard: {
    backgroundColor: "transparent",
    border: "none",
    boxShadow: "none",
    marginBottom: "20px",
    cursor: "pointer",
    transition: "transform 0.3s ease",
    "&:hover": {
      transform: "translateY(-2px)"
    }
  },
  relatedPostImage: {
    width: "100%",
    height: "150px",
    objectFit: "cover",
    borderRadius: "6px",
    marginBottom: "12px"
  },
  relatedPostPlaceholder: {
    width: "100%",
    height: "150px",
    backgroundColor: "#f5f5f5",
    borderRadius: "6px",
    marginBottom: "12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#999",
    fontSize: "0.8rem"
  },
  relatedPostTitle: {
    fontSize: "1rem",
    fontWeight: "500",
    color: "#3f444b",
    textDecoration: "none",
    lineHeight: "1.3",
    marginBottom: "8px",
    "&:hover": {
      color: "#f7931e",
      textDecoration: "none"
    }
  },
  relatedPostMeta: {
    fontSize: "0.75rem",
    color: "#999"
  }
});

export default function BlogPost() {
  const classes = useStyles();
  const { id } = useParams();
  const [post, setPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        setLoading(true);
        const response = await blogApi.getPostById(id);
        
        if (response.success) {
          setPost(response.data);
          
          // Fetch related posts
          try {
            const relatedResponse = await blogApi.getRelatedPosts(id, 3);
            if (relatedResponse.success) {
              setRelatedPosts(relatedResponse.data);
            }
          } catch (relatedErr) {
            console.error('Error fetching related posts:', relatedErr);
            // Don't set error state for related posts failure
          }
        } else {
          throw new Error(response.message || 'Failed to fetch post');
        }
      } catch (err) {
        setError(err.message);
        console.error('Error fetching post:', err);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchPost();
    }
  }, [id]);

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const renderContent = () => {
    if (loading) {
      return <div className={classes.loadingText}>Loading post...</div>;
    }

    if (error) {
      return <div className={classes.errorText}>Error loading post: {error}</div>;
    }

    if (!post) {
      return <div className={classes.notFoundText}>Post not found.</div>;
    }

    return (
      <div className={classes.postCard}>
        {post.featured_image && (
          <img 
            src={post.featured_image} 
            alt={post.title}
            className={classes.featuredImage}
          />
        )}
        
        <h1 className={classes.postTitle}>{post.title}</h1>
        
        <div className={classes.postMeta}>
          {post.author && (
            <div className={classes.authorSection}>
              {post.author.avatar && (
                <img 
                  src={post.author.avatar} 
                  alt={post.author.name}
                  className={classes.authorAvatar}
                />
              )}
              <span className={classes.authorName}>By {post.author.name}</span>
            </div>
          )}
          <div>
            Published: {formatDate(post.created_at)}
            {post.updated_at !== post.created_at && (
              <span> | Updated: {formatDate(post.updated_at)}</span>
            )}
          </div>
          {post.tags && post.tags.length > 0 && (
            <div className={classes.tagsSection}>
              {post.tags.map((tag) => (
                <span key={tag.id} className={classes.tag}>
                  {tag.name}
                </span>
              ))}
            </div>
          )}
        </div>
        
        <div 
          className={`${classes.postContent} blog-content`}
          dangerouslySetInnerHTML={{ __html: post.content }}
        />
        
        {relatedPosts.length > 0 && (
          <div className={classes.relatedPostsSection}>
            <h2 className={classes.relatedPostsTitle}>Related Posts</h2>
            <GridContainer>
              {relatedPosts.map((relatedPost) => (
                <GridItem xs={12} sm={6} md={4} key={relatedPost.id}>
                  <div className={classes.relatedPostCard}>
                    <Link to={`/blog/post/${relatedPost.id}`} style={{ textDecoration: 'none' }}>
                      {relatedPost.featured_image ? (
                        <img 
                          src={relatedPost.featured_image} 
                          alt={relatedPost.title}
                          className={classes.relatedPostImage}
                        />
                      ) : (
                        <div className={classes.relatedPostPlaceholder}>
                          No Image
                        </div>
                      )}
                      
                      <h3 className={classes.relatedPostTitle}>
                        {relatedPost.title}
                      </h3>
                      
                      <div className={classes.relatedPostMeta}>
                        {formatDate(relatedPost.created_at)}
                      </div>
                    </Link>
                  </div>
                </GridItem>
              ))}
            </GridContainer>
          </div>
        )}
      </div>
    );
  };

  return (
    <div>
      <Header
        brand="WeVett Realty"
        links={<HeaderLinks dropdownHoverColor="primary" />}
        fixed
        color="transparent"
        changeColorOnScroll={{
          height: 80,
          color: "secondary"
        }}
      />
      
      <div className={classes.main}>
        <div className={classes.container}>
          <div className={classes.backLink}>
            <Link to="/blog">
              <div className={classes.backButton}>
                <i class="fa-solid fa-angle-left"></i><span>Back to Blog</span>
              </div>
            </Link>
          </div>
          
          <GridContainer>
            <GridItem xs={12} sm={12} md={12}>
              {renderContent()}
            </GridItem>
          </GridContainer>
        </div>
      </div>
      
      <CustomFooter />
    </div>
  );
}